import { useCallback, useEffect, useRef, useState } from 'react';

// One selected source, owned only by HitLabLanding. No Radio listening/reward telemetry.
export default function useHitLabVoicePreview(onBeforePlay) {
  const audioRef = useRef(null);
  const requestRef = useRef(0);
  const selectedRef = useRef(null);
  const playingRequestedRef = useRef(false);
  const [state, setState] = useState({ clipKey: '', status: 'idle', elapsed: 0, duration: 0 });

  const pause = useCallback(() => {
    requestRef.current += 1;
    playingRequestedRef.current = false;
    audioRef.current?.pause();
    setState((current) => ['playing', 'loading'].includes(current.status)
      ? { ...current, status: 'paused' } : current);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      requestRef.current += 1;
      playingRequestedRef.current = false;
      audio?.pause();
      audio?.removeAttribute('src');
      audio?.load();
    };
  }, []);

  const toggle = async (clip) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (selectedRef.current?.clipKey === clip.clipKey && playingRequestedRef.current) {
      pause();
      return;
    }
    onBeforePlay();
    const request = ++requestRef.current;
    audio.pause();
    if (selectedRef.current?.clipKey !== clip.clipKey || selectedRef.current?.audioUrl !== clip.audioUrl ||
        (state.clipKey === clip.clipKey && state.status === 'error')) {
      selectedRef.current = clip;
      audio.src = clip.audioUrl;
      audio.load();
    } else if (audio.ended) {
      audio.currentTime = 0;
    }
    playingRequestedRef.current = true;
    setState({ clipKey: clip.clipKey, status: 'loading', elapsed: Number(audio.currentTime) || 0, duration: clip.durationSeconds });
    try {
      await audio.play();
      if (request === requestRef.current && playingRequestedRef.current) {
        setState((current) => ({ ...current, status: 'playing' }));
      }
    } catch {
      if (request === requestRef.current) {
        playingRequestedRef.current = false;
        setState((current) => ({ ...current, status: 'error' }));
      }
    }
  };

  const isCurrentSource = () => {
    const audio = audioRef.current;
    return selectedRef.current && audio && (!audio.currentSrc || audio.currentSrc === audio.src);
  };
  const onTimeUpdate = () => {
    if (!isCurrentSource()) return;
    const elapsed = Number(audioRef.current.currentTime);
    if (Number.isFinite(elapsed)) setState((current) => ({ ...current, elapsed }));
  };
  const onPlaying = () => {
    if (!playingRequestedRef.current) { audioRef.current?.pause(); return; }
    if (isCurrentSource()) setState((current) => ({ ...current, status: 'playing' }));
  };
  const onWaiting = () => {
    if (playingRequestedRef.current && isCurrentSource()) setState((current) => ({ ...current, status: 'loading' }));
  };
  const finish = (status) => {
    if (!playingRequestedRef.current || !isCurrentSource()) return;
    requestRef.current += 1;
    playingRequestedRef.current = false;
    audioRef.current?.pause();
    setState((current) => ({ ...current, status, elapsed: status === 'ended' ? current.duration : current.elapsed }));
  };

  return { state, toggle, pause, audioProps: {
    ref: audioRef, preload: 'none', 'aria-label': 'Listener voice preview audio',
    onTimeUpdate, onPlaying, onWaiting, onEnded: () => finish('ended'), onError: () => finish('error'),
  } };
}
